import React, { useEffect, useState } from "react";
import { FileText, Trash2, Upload } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

const DOC_TYPES = ["welcomeVideo", "subscription", "fixedDeposit", "contract", "statement", "kyc", "other"];

export function ClientDocumentsPanel({ clientId }) {
  const { t } = useLanguage();
  const [docs, setDocs] = useState([]);
  const [title, setTitle] = useState("");
  const [docType, setDocType] = useState("subscription");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/clients/${clientId}/documents`).then(({ data }) => setDocs(data)).catch(() => {});
  useEffect(() => { load(); }, [clientId]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("clientId", clientId); fd.append("title", title || file.name); fd.append("docType", docType); fd.append("file", file);
      await api.post("/admin/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTitle(""); setFile(null);
      document.getElementById("docfile").value = "";
      load();
    } finally { setBusy(false); }
  };

  const remove = async (id) => { await api.delete(`/admin/documents/${id}`); load(); };

  return (
    <div className="px-5 py-5">
      <p className="text-sm text-muted-foreground">{t("admin.docs.hint")}</p>
      <form onSubmit={upload} className="mt-4 grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2"><Label htmlFor="dtitle">{t("admin.docs.docTitle")}</Label><Input id="dtitle" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
        <div className="flex flex-col gap-2"><Label>{t("admin.docs.docType")}</Label>
          <Select value={docType} onValueChange={setDocType}><SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{DOC_TYPES.map((d) => <SelectItem key={d} value={d}>{t(`docType.${d}`)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-2 sm:col-span-2"><Label htmlFor="docfile">{t("admin.docs.file")}</Label>
          <Input id="docfile" type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.mp4,.mov" />
        </div>
        <div className="sm:col-span-2"><Button type="submit" disabled={busy || !file}><Upload className="h-4 w-4" />{busy ? t("admin.docs.uploading") : t("admin.docs.save")}</Button></div>
      </form>

      <div className="mt-6">
        {docs.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">{t("admin.docs.empty")}</p> : (
          <ul className="divide-y divide-border">
            {docs.map((d) => (
              <li key={d.id} className="flex items-center gap-4 py-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent"><FileText className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium text-foreground">{d.title}</p><p className="truncate text-xs text-muted-foreground">{t(`docType.${d.docType}`)} · {d.fileName}</p></div>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(d.id)}><Trash2 className="h-4 w-4" /></Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
